import React, { useState, useEffect } from 'react';

// Authorized users with their passwords
const ALLOWED_USERS = {
  'sandeep@incrementumx.com': 'SandyOps2024!',
  'zon@incrementumx.com': 'ZonOps2024!',
  'aditya@incrementumx.com': 'AdiOps2024!'
};

// Admin credentials (shared access)
const ADMIN_CREDENTIALS = {
  email: 'admin@incrementumx.com',
  password: 'IncXAdmin2024!'
};

function AuthGate({ children }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginMode, setLoginMode] = useState('user'); // 'user' or 'admin'

  useEffect(() => {
    const storedAuth = localStorage.getItem('ops_dashboard_auth');
    if (storedAuth) {
      try {
        const auth = JSON.parse(storedAuth);
        // Check if session is still valid (24 hours)
        const sessionAge = Date.now() - auth.timestamp;
        const maxAge = 24 * 60 * 60 * 1000; // 24 hours
        
        if (sessionAge < maxAge) {
          setIsAuthenticated(true);
        } else {
          localStorage.removeItem('ops_dashboard_auth');
        }
      } catch (e) {
        localStorage.removeItem('ops_dashboard_auth');
      }
    }
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    const normalizedEmail = email.trim().toLowerCase();
    
    let isValid = false;
    let userType = '';
    
    if (loginMode === 'admin') {
      // Admin login
      if (normalizedEmail === ADMIN_CREDENTIALS.email && password === ADMIN_CREDENTIALS.password) {
        isValid = true;
        userType = 'admin';
      }
    } else {
      // User login
      if (ALLOWED_USERS[normalizedEmail] && ALLOWED_USERS[normalizedEmail] === password) {
        isValid = true;
        userType = 'user';
      }
    }
    
    if (isValid) {
      const authData = {
        email: normalizedEmail,
        type: userType,
        timestamp: Date.now()
      };
      localStorage.setItem('ops_dashboard_auth', JSON.stringify(authData));
      setIsAuthenticated(true);
      setError('');
    } else {
      setError('Invalid email or password. Please try again.');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('ops_dashboard_auth');
    setIsAuthenticated(false);
    setEmail('');
    setPassword('');
  };

  const toggleLoginMode = () => {
    setLoginMode(loginMode === 'user' ? 'admin' : 'user');
    setEmail('');
    setPassword('');
    setError('');
  };

  const getAuthInfo = () => {
    const storedAuth = localStorage.getItem('ops_dashboard_auth');
    if (storedAuth) {
      try {
        return JSON.parse(storedAuth);
      } catch (e) {
        return null;
      }
    }
    return null;
  };

  if (!isAuthenticated) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <h1 style={styles.title}>🔒 IncX Operations Dashboard</h1>
          <p style={styles.subtitle}>Restricted Access</p>
          
          {/* Login Mode Toggle */}
          <div style={styles.toggleContainer}>
            <button
              type="button"
              onClick={() => setLoginMode('user')}
              style={{
                ...styles.toggleBtn,
                ...(loginMode === 'user' ? styles.toggleBtnActive : {})
              }}
            >
              User Login
            </button>
            <button
              type="button"
              onClick={() => setLoginMode('admin')}
              style={{
                ...styles.toggleBtn,
                ...(loginMode === 'admin' ? styles.toggleBtnActive : {})
              }}
            >
              Admin Access
            </button>
          </div>
          
          <form onSubmit={handleSubmit} style={styles.form}>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={loginMode === 'admin' ? "Admin email" : "Your email"}
              style={styles.input}
              required
            />
            <div style={styles.passwordContainer}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                style={styles.passwordInput}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={styles.showPasswordBtn}
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
            <button type="submit" style={styles.button}>
              {loginMode === 'admin' ? 'Admin Access' : 'Access Dashboard'}
            </button>
          </form>
          
          {error && <p style={styles.error}>{error}</p>}
        </div>
      </div>
    );
  }

  const authInfo = getAuthInfo();

  return (
    <>
      <div style={styles.header}>
        <span style={styles.userEmail}>
          👤 {authInfo?.email} {authInfo?.type === 'admin' && <span style={styles.adminBadge}>ADMIN</span>}
        </span>
        <button onClick={handleLogout} style={styles.logoutBtn}>
          Logout
        </button>
      </div>
      {children}
    </>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    fontFamily: 'system-ui, -apple-system, sans-serif'
  },
  card: {
    background: 'white',
    padding: '40px',
    borderRadius: '12px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    width: '100%',
    maxWidth: '420px',
    textAlign: 'center'
  },
  title: {
    margin: '0 0 8px 0',
    fontSize: '24px',
    color: '#333'
  },
  subtitle: {
    margin: '0 0 20px 0',
    color: '#666',
    fontSize: '14px'
  },
  toggleContainer: {
    display: 'flex',
    gap: '8px',
    marginBottom: '20px',
    padding: '4px',
    background: '#f0f0f0',
    borderRadius: '8px'
  },
  toggleBtn: {
    flex: 1,
    padding: '8px 16px',
    borderRadius: '6px',
    border: 'none',
    background: 'transparent',
    color: '#666',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  toggleBtnActive: {
    background: 'white',
    color: '#667eea',
    fontWeight: '600',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  input: {
    padding: '12px 16px',
    borderRadius: '8px',
    border: '1px solid #ddd',
    fontSize: '16px',
    outline: 'none'
  },
  passwordContainer: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center'
  },
  passwordInput: {
    flex: 1,
    padding: '12px 16px',
    paddingRight: '45px',
    borderRadius: '8px',
    border: '1px solid #ddd',
    fontSize: '16px',
    outline: 'none'
  },
  showPasswordBtn: {
    position: 'absolute',
    right: '12px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '18px',
    padding: '0'
  },
  button: {
    padding: '12px 24px',
    borderRadius: '8px',
    border: 'none',
    background: '#667eea',
    color: 'white',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  error: {
    color: '#e74c3c',
    marginTop: '16px',
    fontSize: '14px'
  },
  header: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 24px',
    background: '#f8f9fa',
    borderBottom: '1px solid #e9ecef'
  },
  userEmail: {
    color: '#666',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  adminBadge: {
    background: '#dc3545',
    color: 'white',
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: '600'
  },
  logoutBtn: {
    padding: '6px 12px',
    borderRadius: '4px',
    border: '1px solid #ddd',
    background: 'white',
    color: '#666',
    fontSize: '12px',
    cursor: 'pointer'
  }
};

export default AuthGate;